import StreamingAvatar, { AvatarQuality, StreamingEvents, TaskType, VoiceEmotion } from '@heygen/streaming-avatar'
import { useEffect, useRef, useState } from 'react'
import { usePrevious } from '../../utility/hooks'

type StartSession = () => Promise<StreamingAvatar | null>
type HandleSpeak = (text: string) => Promise<void>
type HandleInterrupt = () => Promise<void>
type CloseSession = () => Promise<void>

export const useHeygenRealtime = (
    apiKey: string,
    mediaStream: React.RefObject<HTMLVideoElement | null>,
    avatarId: string,
    language: string
): [
    boolean,
    boolean,
    boolean,
    StreamingAvatar | null,
    StartSession,
    HandleSpeak,
    HandleInterrupt,
    CloseSession,
    boolean
] => {
    const [text, setText] = useState<string>('')
    const [session, setSession] = useState<StreamingAvatar | null>(null)
    const [isLoadingSession, setIsLoadingSession] = useState(false)
    const [isLoadingRepeat, setIsLoadingRepeat] = useState(false)
    const [stream, setStream] = useState<MediaStream>()
    const [isReadyToPlay, setIsReadyToPlay] = useState(false)
    const [isSpeaking, setIsSpeaking] = useState(false)
    const avatar = useRef<StreamingAvatar | null>(null)

    async function fetchAccessToken() {
        try {
            const res = await fetch('https://api.heygen.com/v1/streaming.create_token', {
                method: 'POST',
                headers: {
                    'x-api-key': apiKey,
                },
            })
            const data = await res.json().then((data) => data.data)

            console.debug('Heygen Access Token:', data.token) // Log the token to verify

            return data.token
        } catch (error) {
            console.error('Error fetching access token:', error)
        }

        return ''
    }

    const startSession: StartSession = async (): Promise<StreamingAvatar | null> => {
        setIsLoadingSession(true)

        try {
            const newToken = await fetchAccessToken()
            const instance = new StreamingAvatar({
                token: newToken,
            })

            avatar.current = instance

            avatar.current.on(StreamingEvents.AVATAR_START_TALKING, (e) => {
                console.debug('Avatar is speaking', e)
                setIsSpeaking(true)
            })

            avatar.current.on(StreamingEvents.AVATAR_STOP_TALKING, (e) => {
                console.debug('Avatar stopped speaking', e)
                setIsSpeaking(false)
            })

            avatar.current.on(StreamingEvents.STREAM_DISCONNECTED, async () => {
                setSession(null)
                await endSession()
            })

            avatar.current?.on(StreamingEvents.STREAM_READY, (event) => {
                setStream(event.detail)
            })

            await avatar.current.createStartAvatar({
                quality: AvatarQuality.Medium,
                avatarName: avatarId,
                voice: {
                    rate: 1,
                    emotion: VoiceEmotion.EXCITED,
                },
                language,
            })
            setSession(instance)
            return instance
        } catch (error) {
            return null
        } finally {
            setIsLoadingSession(false)
        }
    }
    const handleSpeak: HandleSpeak = async (text: string) => {
        setText(text)
        setIsLoadingRepeat(true)
        if (!avatar.current) {
            console.error('Avatar API not initialized')
            return
        }
        await avatar.current
            .speak({
                text,
                task_type: TaskType.REPEAT,
            })
            .catch((e) => {
                console.error(e.message)
            })
        setIsLoadingRepeat(false)
    }
    const handleInterrupt: HandleInterrupt = async () => {
        if (!avatar.current) {
            console.error('Avatar API not initialized')

            return
        }
        await avatar.current.interrupt().catch((e) => {
            console.error(e.message)
        })
    }
    const endSession: CloseSession = async () => {
        await avatar.current?.stopAvatar()
        mediaStream.current.srcObject = null
        setStream(undefined)
        setSession(null)
    }

    const previousText = usePrevious(text)
    useEffect(() => {
        if (!previousText && text) {
            void avatar.current?.startListening()
        } else if (previousText && !text) {
            void avatar?.current?.stopListening()
        }
    }, [text, previousText])

    useEffect(() => {
        return () => {
            void endSession()
        }
    }, [])

    useEffect(() => {
        if (stream && mediaStream.current) {
            mediaStream.current.srcObject = stream

            mediaStream.current.onloadedmetadata = () => {
                void mediaStream.current?.play()
                mediaStream.current.muted = false
                mediaStream.current.volume = 1

                setTimeout(() => {
                    setIsReadyToPlay(true)
                }, 1000)

                setTimeout(() => {
                    void mediaStream.current?.play()
                }, 2000)
            }
        }
    }, [mediaStream, stream])

    return [
        isReadyToPlay,
        isLoadingSession,
        isLoadingRepeat,
        session,
        startSession,
        handleSpeak,
        handleInterrupt,
        endSession,
        isSpeaking,
    ]
}
